import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { CheckCircle, Download, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { bulkFetchLeads } from "../../services/crmServices/leadService";
import * as XLSX from "xlsx";

export function ExportLeadsModal({ open, onClose, selectedLeads, leadStatuses }) {
    const [status, setStatus] = useState("preparing"); // preparing, fetching, generating, ready, error
    const [progress, setProgress] = useState(0);
    const [fetchedLeads, setFetchedLeads] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [excelFile, setExcelFile] = useState(null);

    useEffect(() => {
        if (open && selectedLeads.length > 0) {
            startExport();
        }
    }, [open, selectedLeads]);

    const startExport = async () => {
        try {
            setStatus("preparing");
            setProgress(10);

            console.log("=== EXPORT START ===");
            console.log("Total selected leads:", selectedLeads.length);
            console.log("Sample lead:", selectedLeads[0]);

            // Helper to get full name from user object
            const getFullName = (user) => {
                if (!user) return "";
                if (typeof user === 'string') return user;
                if (user.firstName || user.lastName) {
                    return `${user.firstName || ""} ${user.lastName || ""}`.trim();
                }
                return user.name || "";
            };

            // Helper to get lead age
            const getLeadAge = (createdAt) => {
                if (!createdAt) return "-";
                const now = new Date();
                const created = new Date(createdAt);
                const diffTime = now - created;
                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                
                if (diffDays >= 1 && diffDays <= 14) {
                    return "Fresh";
                } else if (diffDays > 14 && diffDays <= 28) {
                    return "Warm";
                } else if (diffDays > 28) {
                    return "Old";
                } else {
                    return "Fresh";
                }
            };

            // Helper to get status label
            const getStatusLabel = (statusValue) => {
                if (!statusValue) return "-";
                const status = leadStatuses.find(s => s.value === statusValue);
                return status ? status.label : statusValue;
            };

            // Check which leads need to be fetched
            const leadsNeedingFetch = selectedLeads.filter(lead => Object.keys(lead).length === 1 && lead._id);
            const leadsWithFullData = selectedLeads.filter(lead => Object.keys(lead).length > 1);

            console.log("Leads needing fetch:", leadsNeedingFetch.length);
            console.log("Leads with full data:", leadsWithFullData.length);

            let allLeadsData = [...leadsWithFullData];
            setProgress(20);

            // Fetch missing leads if needed
            if (leadsNeedingFetch.length > 0) {
                setStatus("fetching");
                setProgress(30);

                const leadIdsToFetch = leadsNeedingFetch.map(lead => lead._id);
                
                // Fetch in chunks if there are many leads (to avoid timeout)
                const chunkSize = 500;
                const chunks = [];
                for (let i = 0; i < leadIdsToFetch.length; i += chunkSize) {
                    chunks.push(leadIdsToFetch.slice(i, i + chunkSize));
                }

                for (let i = 0; i < chunks.length; i++) {
                    const chunk = chunks[i];
                    console.log(`Fetching chunk ${i + 1}/${chunks.length} (${chunk.length} leads)`);
                    const response = await bulkFetchLeads(chunk);
                    
                    if (response?.status === 200 && response.data) {
                        console.log(`Received ${response.data.length} leads from server`);
                        if (i === 0) {
                            console.log("First chunk sample lead:", response.data[0]);
                        }
                        allLeadsData = [...allLeadsData, ...response.data];
                    } else {
                        console.error("Fetch failed:", response);
                        throw new Error("Failed to fetch lead data from server");
                    }

                    // Update progress
                    const fetchProgress = 30 + (40 * (i + 1) / chunks.length);
                    setProgress(Math.round(fetchProgress));
                }
            } else {
                setProgress(70);
            }

            console.log("Total leads after fetching:", allLeadsData.length);
            console.log("First fetched lead:", allLeadsData[0]);
            console.log("Last fetched lead:", allLeadsData[allLeadsData.length - 1]);

            // Generate Excel file
            setStatus("generating");
            setProgress(75);

            const excelData = allLeadsData.map((lead, index) => {
                if (index === 0 || index === allLeadsData.length - 1) {
                    console.log(`Lead ${index}:`, lead);
                }
                // Get Floor Manager name
                const floorManagerName = getFullName(lead.floorManager) || 
                                         (lead.desk?.floorManager ? getFullName(lead.desk.floorManager) : "-");

                return {
                    "Calls": lead.callCount || 0,
                    "Name": `${lead.firstName || ""} ${lead.lastName || ""}`.trim() || "-",
                    "Status": getStatusLabel(lead.status),
                    "Email": lead.email || "-",
                    "Phone": lead.phone || "-",
                    "Age": getLeadAge(lead.createdAt),
                    "Desk": lead.desk?.name || "-",
                    "Floor": floorManagerName,
                    "Agent": getFullName(lead.agent) || "Unassigned",
                    "Source": lead.source || "-",
                    "Country": lead.country || "-",
                    "Last Comment": lead.lastComment || "-"
                };
            });

            setProgress(85);

            console.log("Excel data rows:", excelData.length);
            console.log("First Excel row:", excelData[0]);
            console.log("Last Excel row:", excelData[excelData.length - 1]);
            
            // Check for any undefined/null rows
            const emptyRows = excelData.filter(row => !row || !row.Name || row.Name === "-");
            console.log("Empty rows count:", emptyRows.length);
            if (emptyRows.length > 0) {
                console.log("Sample empty row:", emptyRows[0]);
            }

            // Create workbook - use explicit method to ensure all data is written
            const worksheet = XLSX.utils.json_to_sheet(excelData, {
                header: ["Calls", "Name", "Status", "Email", "Phone", "Age", "Desk", "Floor", "Agent", "Source", "Country", "Last Comment"],
                skipHeader: false
            });
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
            
            console.log("Worksheet ref:", worksheet['!ref']);
            console.log("Worksheet range:", XLSX.utils.decode_range(worksheet['!ref']));

            // Auto-size columns
            const maxWidth = 50;
            const columnWidths = Object.keys(excelData[0] || {}).map(key => {
                const maxLength = Math.max(
                    key.length,
                    ...excelData.map(row => String(row[key] || "").length)
                );
                return { wch: Math.min(maxLength + 2, maxWidth) };
            });
            worksheet['!cols'] = columnWidths;

            setProgress(95);

            // Generate filename with timestamp and count
            const now = new Date();
            const timestamp = now.toISOString().replace(/[:.]/g, '-').replace('T', '_').substring(0, 19);
            const fileName = `CRM_Leads_${allLeadsData.length}_leads_${timestamp}.xlsx`;

            console.log("Excel file ready:", fileName);
            console.log("Workbook sheets:", workbook.SheetNames);
            console.log("Total rows in workbook:", allLeadsData.length + 1); // +1 for header

            // Store file info for download
            setExcelFile({ workbook, fileName, count: allLeadsData.length });
            setFetchedLeads(allLeadsData);
            setProgress(100);
            setStatus("ready");

        } catch (error) {
            console.error("Error exporting leads:", error);
            setStatus("error");
            setErrorMessage(error.message || "Failed to export leads. Please try again.");
        }
    };

    const handleDownload = () => {
        if (excelFile) {
            console.log("=== DOWNLOADING EXCEL FILE ===");
            console.log("Filename:", excelFile.fileName);
            console.log("Lead count:", excelFile.count);
            console.log("Workbook sheets:", excelFile.workbook.SheetNames);
            
            const sheet = excelFile.workbook.Sheets["Leads"];
            console.log("Sheet ref (range):", sheet['!ref']);
            console.log("Sample cells from sheet:");
            console.log("A1 (header):", sheet['A1']);
            console.log("A2 (first data):", sheet['A2']);
            console.log("B2 (first name):", sheet['B2']);
            
            XLSX.writeFile(excelFile.workbook, excelFile.fileName);
            console.log("File written successfully!");
            
            toast.success(`Downloaded ${excelFile.count} leads successfully!`);
            onClose();
        }
    };

    const handleClose = () => {
        // Reset state
        setStatus("preparing");
        setProgress(0);
        setFetchedLeads([]);
        setErrorMessage("");
        setExcelFile(null);
        onClose();
    };

    const getStatusText = () => {
        switch (status) {
            case "preparing":
                return "Preparing export...";
            case "fetching":
                return `Fetching ${selectedLeads.length} leads from server...`;
            case "generating":
                return "Generating Excel file...";
            case "ready":
                return "Excel file ready to download!";
            case "error":
                return "Export failed";
            default:
                return "Processing...";
        }
    };

    const getStatusIcon = () => {
        switch (status) {
            case "ready":
                return <CheckCircle className="h-12 w-12 text-green-600" />;
            case "error":
                return <AlertCircle className="h-12 w-12 text-red-600" />;
            default:
                return <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />;
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Export Leads to Excel</DialogTitle>
                    <DialogDescription>
                        {status === "error" 
                            ? "An error occurred during export"
                            : status === "ready"
                            ? `Successfully prepared ${excelFile?.count || 0} leads for export`
                            : `Exporting ${selectedLeads.length} selected leads`}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col items-center justify-center py-8 space-y-6">
                    {/* Status Icon */}
                    <div className="flex items-center justify-center">
                        {getStatusIcon()}
                    </div>

                    {/* Status Text */}
                    <div className="text-center">
                        <h3 className="text-lg font-semibold mb-2">
                            {getStatusText()}
                        </h3>
                        {status === "error" && (
                            <p className="text-sm text-red-600">{errorMessage}</p>
                        )}
                        {status === "fetching" && (
                            <p className="text-sm text-gray-600">
                                This may take a moment for large datasets
                            </p>
                        )}
                        {status === "preparing" && selectedLeads.length <= 50 && (
                            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-sm text-yellow-800">
                                    ⚠️ Only {selectedLeads.length} leads selected. 
                                    <br />
                                    To export ALL filtered leads, use the "Select All" checkbox above the table.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Progress Bar */}
                    {status !== "error" && status !== "ready" && (
                        <div className="w-full space-y-2">
                            <Progress value={progress} className="w-full" />
                            <p className="text-sm text-gray-600 text-center">
                                {progress}% complete
                            </p>
                        </div>
                    )}

                    {/* Download Button */}
                    {status === "ready" && (
                        <div className="flex flex-col items-center space-y-3">
                            <div className="text-center px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-sm font-semibold text-green-800">
                                    ✅ Excel file ready with {excelFile?.count || 0} leads
                                </p>
                                <p className="text-xs text-green-600 mt-1">
                                    File: {excelFile?.fileName}
                                </p>
                            </div>
                            <Button
                                onClick={handleDownload}
                                className=""
                                size="lg"
                            >
                                <Download className="h-5 w-5 mr-2" />
                                Download Excel File ({excelFile?.count || 0} leads)
                            </Button>
                        </div>
                    )}

                    {/* Error Retry */}
                    {status === "error" && (
                        <div className="flex space-x-3">
                            <Button
                                onClick={startExport}
                                variant="outline"
                            >
                                Try Again
                            </Button>
                            <Button
                                onClick={handleClose}
                                variant="ghost"
                            >
                                Close
                            </Button>
                        </div>
                    )}

                    {/* Cancel Button (only show while processing) */}
                    {status !== "ready" && status !== "error" && (
                        <Button
                            onClick={handleClose}
                            variant="ghost"
                            size="sm"
                        >
                            Cancel
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
